import * as _ from 'lodash';

import * as API from '../api';
import type { Category, FilterByCategory, IssueByCategory } from '../../../shared/issuesV2';

import type {
  IssueList,
  IssuesV2BaseStore,
  IssuesV2Store,
  LicenseGroups,
  LicenseGroupWithLicenses,
  RevisionLicensesResponse,
  RevisionLicenseWithGroup,
} from './types';

/**
 * Wrapper around our get and put helpers, but with better
 * defaults for issuesV2-related stuff.
 * Specifically, it always includes dedupe and encoding params.
 */
export const get = <R>(endpoint: string, params: Record<string, unknown> = {}) =>
  API.get<R>(endpoint, true, params, { encodeQuery: true });

export const put = <A, R>(endpoint: string, action: A, params: Record<string, unknown> = {}) =>
  API.put<A, R>(endpoint, action, { ...params, encodeQuery: true });

export const post = <A>(endpoint: string, action: A, params: Record<string, unknown> = {}) =>
  API.post<A, void>(endpoint, action, { ...params, encodeQuery: true });

export const remove = <A, R>(endpoint: string, params: Record<string, unknown> = {}) =>
  API.remove<A, R>(endpoint, { ...params, encodeQuery: true });

/**
 * Helps separate logic based on the current grouping.  Given the whole IssueV2
 * state it will run one of the two functions provided depending on the
 * grouping.  The argument to that function are the items type-scoped to the
 * current grouping.
 */
export function byGrouping<
  T,
  C extends Category,
  I extends IssueByCategory<C>,
  S extends Pick<IssuesV2BaseStore<C, FilterByCategory<C>, I>, 'items'>,
>(
  state: S,
  onByIssue: (items: S['items'] & { grouping: 'issue' }) => T,
  onByRevision: (items: S['items'] & { grouping: 'revision' }) => T,
): T {
  if (state.items.grouping === 'issue') {
    return onByIssue(state.items);
  }
  return onByRevision(state.items);
}

/** A getter for instances where we are working directly with the store. */
export const getIssuesFromStore = (
  state: IssuesV2Store<Category>,
): IssueList =>
  state.items.grouping === 'issue'
    ? state.items.issues
    : (state.items.revisions.value?.flatMap<IssueByCategory<Category>>(revision => revision.issues) ?? []) as IssueList;

/**
 * Reducer helper to group licenses by license group.
 */
const reduceLicensesByLicenseGroup = (
  acc: Record<number, LicenseGroupWithLicenses>,
  curr: RevisionLicenseWithGroup,
) => {
  // If the license doesn't have a license group, skip it.
  if (!curr.licenseGroupId) {
    return acc;
  }
  // If the license group doesn't exist yet, create it with an empty list of licenses.
  acc[curr.licenseGroupId] = acc[curr.licenseGroupId] || {
    ...curr.group,
    licenses: [],
  };
  // Add the license to the license group. Array should not be empty at this point.
  acc[curr.licenseGroupId]?.licenses.push(_.omit(curr, 'group'));

  return acc;
};

/**
 * Group licenses by license group.
 */
export const groupLicensesByLicenseGroup = (licenses: RevisionLicensesResponse): LicenseGroups => {
  const groupedLicenses = licenses.reduce(reduceLicensesByLicenseGroup, {});
  return Object.values(groupedLicenses);
};
